import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchSession } from '../api/auth';

const AUTH_USER_KEY = 'authUser';

const initialState = {
  id: null,
  email: '',
  superadmin: false,
  agency: null,
  authorizePermissions: [],
  isLoggedIn: false,
  isLoadingUser: false,
  loginError: null,
};

function parseJwt(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export const fetchUser = createAsyncThunk('user/fetch', async (token) => {
  const jwtData = parseJwt(token);
  const expiresAt = jwtData?.exp ? jwtData.exp * 1000 : null;

  if (expiresAt && expiresAt <= Date.now()) {
    throw new Error('Token expired');
  }

  try {
    return await fetchSession();
  } catch (error) {
    if (error?.response?.status !== 404) {
      throw error;
    }
  }

  const persistedUser = window.localStorage.getItem(AUTH_USER_KEY);
  if (!persistedUser) {
    throw new Error('Missing persisted user');
  }

  return JSON.parse(persistedUser);
});

const normalizeSessionPayload = (payload) => {
  const user = payload?.user || {};
  return {
    id: user.id ?? null,
    email: user.email ?? '',
    superadmin: Boolean(user.superadmin),
    agency: user.agency ?? null,
    authorizePermissions: payload?.permissions ?? [],
  };
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    reset: (state) => {
      Object.assign(state, initialState);
    },
    setUser: (state, { payload }) => {
      state.isLoggedIn = true;
      state.loginError = null;
      Object.assign(state, payload);
    },
    setLoginError: (state, { payload }) => {
      state.loginError = payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.isLoadingUser = true;
      })
      .addCase(fetchUser.fulfilled, (state, { payload }) => {
        state.isLoggedIn = true;
        state.isLoadingUser = false;
        state.loginError = null;
        Object.assign(state, normalizeSessionPayload(payload));
      })
      .addCase(fetchUser.rejected, (state, { error }) => {
        state.isLoadingUser = false;
        state.isLoggedIn = false;

        if (error?.message && error.message.includes('401')) {
          state.loginError = 'Tu cuenta esta bloqueada.';
        }
      });
  },
});

export const { reset, setUser, setLoginError } = userSlice.actions;

export default userSlice.reducer;
